/**
 * Adapter registry for per-type media unit import adapters.
 *
 * Design decision D-6 (design.md): The registry lives in contracts so both
 * the worker (dispatch) and the API (per-type required-column metadata) can
 * import it without crossing a process boundary.
 *
 * One adapter per MediaTypeEnum value. Exhaustiveness is enforced by the
 * registry contract test in __tests__/registry.test.ts.
 */
import type { MediaType } from '../../schemas/media-unit-variants.js';
import type { MediaUnitImportAdapter } from './types.js';

import { normalizeMediaType } from '../normalize-media-type.js';
import { billboardAdapter } from './billboard.adapter.js';
import { backlightAdapter } from './backlight.adapter.js';
import { citylightAdapter } from './citylight.adapter.js';
import { slupAdapter } from './slup.adapter.js';
import { cityscrollAdapter } from './cityscroll.adapter.js';
import { konstrukcjaAdapter } from './konstrukcja.adapter.js';
import { wielkiFormatAdapter } from './wielki-format.adapter.js';
import { muralAdapter } from './mural.adapter.js';
import { doohAdapter } from './dooh.adapter.js';
import { inneAdapter } from './inne.adapter.js';

/**
 * All registered adapters. One entry per MediaTypeEnum value.
 * Add a new entry here whenever MediaTypeEnum gains a new value.
 */
export const ALL_ADAPTERS: readonly MediaUnitImportAdapter[] = [
  billboardAdapter,
  backlightAdapter,
  citylightAdapter,
  slupAdapter,
  cityscrollAdapter,
  konstrukcjaAdapter,
  wielkiFormatAdapter,
  muralAdapter,
  doohAdapter,
  inneAdapter,
] as const;

const ADAPTER_MAP = new Map<string, MediaUnitImportAdapter>(
  ALL_ADAPTERS.map((a) => [normalizeMediaType(a.mediaType), a]),
);

/**
 * Returns the adapter for the given media type, or undefined if not found.
 * Matches case- and whitespace-insensitively so supplier XLSX files using
 * Title Case (e.g. 'Mural', 'Wielki Format') still resolve to the correct
 * adapter registered under the lowercase MediaTypeEnum value.
 *
 * @param mediaType — A MediaTypeEnum value (realigned 10-value set).
 * @returns The matching adapter or undefined.
 */
export function getAdapter(mediaType: MediaType): MediaUnitImportAdapter | undefined {
  return ADAPTER_MAP.get(normalizeMediaType(mediaType));
}

/**
 * Whether width/height are required for a given media type, derived from the
 * same adapter `requiredColumns` the XLSX import already enforces — the single
 * source of truth for per-type dimension requiredness (CU-12472jzd1eb: add/edit
 * forms must behave exactly like import).
 */
export function mediaTypeRequiresDimensions(mediaType: MediaType): boolean {
  const adapter = getAdapter(mediaType);
  return (
    (adapter?.requiredColumns.includes('Szerokość [m]') ?? false) &&
    (adapter?.requiredColumns.includes('Wysokość [m]') ?? false)
  );
}
